import { useCallback, useEffect, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import type { ServiceType } from '@econtractor/types';
import * as apiService from 'api/apiServices';
import { useGlobal } from 'context/GlobalContext';
import { useToast } from 'context/ToastContext';
import { useListRefreshEffect } from 'utils/helpers/list/useListRefreshEffect';
import { useListOnPage } from 'utils/helpers/list/useListOnPage';
import ESpinner from 'components/others/Spinner';
import TitleCounter from 'components/others/TitleCounter';
import ServiceToolBar from './ServiceToolBar';
import ServicesTable from './ServicesTable';
import { useService } from './context/ServiceProvider';
import useCatalogCategoryFilter from '../CatalogCategory/model/useCatalogCategoryFilter';

const defaultServiceFilterStatus = [1];

const Services = () => {
    const { t } = useTranslation(['Services', 'common']);
    const { g } = useGlobal();
    const { showToast } = useToast();
    const {
        showSpinner,
        setShowSpinner,
        servicesDesc,
        servicesOrder,
        setServicesOrder,
        setServicesDesc,
        listResetNonce,
        listRefreshNonce,
    } = useService();

    const [servicesList, setServicesList] = useState<ServiceType[] | null>(null);
    const [page, setPage] = useState<number | null>(null);
    const [filterStatus, setFilterStatus] = useState<number[]>(defaultServiceFilterStatus);
    const { filterCategoryIds, setFilterCategoryIds } = useCatalogCategoryFilter('service');

    const location = useLocation();
    const onPage = useListOnPage();
    const prevListData = useRef<string | null>(null);
    const skipFetchOnStateChangeRef = useRef(false);

    const fetchServices = async (force = false) => {
        if (force) prevListData.current = null;
        const search = `%${g?.search?.[location.pathname] || ''}%`;
        if (!page || showSpinner) return null;

        const listData = {
            search,
            order: `${servicesOrder ? `order by ${servicesOrder} ` : ''}${servicesDesc ? 'DESC' : ''}`,
            page: page < 0 ? 1 : page || 1,
            onPage,
            status: filterStatus,
            categoryIds: filterCategoryIds.length ? filterCategoryIds : undefined,
        };

        if (prevListData.current === JSON.stringify(listData)) return;
        prevListData.current = JSON.stringify(listData);

        setShowSpinner(true);
        const resServices = await apiService.getList(listData);
        if (!resServices) return showToast(t('common:messError'), 'danger');
        setShowSpinner(false);

        g.setPaginator((prev: Record<string, unknown>) => ({
            ...prev,
            [location.pathname]: resServices.paginator,
        }));

        setServicesList(resServices.services);
    };

    const resetServicesList = useCallback(async () => {
        skipFetchOnStateChangeRef.current = true;
        prevListData.current = null;
        setServicesOrder('');
        setServicesDesc(false);
        setFilterStatus(defaultServiceFilterStatus);
        setFilterCategoryIds([]);
        g.setPaginatorPage(location.pathname, 1);
        setPage(1);

        const search = `%${g?.search?.[location.pathname] || ''}%`;
        const listData = {
            search,
            order: '',
            page: 1,
            onPage,
            status: defaultServiceFilterStatus,
        };

        setShowSpinner(true);
        const resServices = await apiService.getList(listData);
        setShowSpinner(false);

        if (!resServices) {
            skipFetchOnStateChangeRef.current = false;
            return showToast(t('common:messError'), 'danger');
        }

        prevListData.current = JSON.stringify(listData);

        g.setPaginator((prev: Record<string, unknown>) => ({
            ...prev,
            [location.pathname]: resServices.paginator,
        }));

        setServicesList(resServices.services);
        skipFetchOnStateChangeRef.current = false;
    }, [g, location.pathname, onPage, setServicesDesc, setServicesOrder, setShowSpinner, showToast, t]);

    useListRefreshEffect(listResetNonce, () => {
        void resetServicesList();
    });

    useListRefreshEffect(listRefreshNonce, () => {
        prevListData.current = null;
        void fetchServices(true);
    });

    useEffect(() => {
        if (page === 1) fetchServices(true);
        else setPage(1);
    }, [g.search?.[location.pathname], g.target]);

    useEffect(() => {
        if (skipFetchOnStateChangeRef.current) return;
        fetchServices();
    }, [page, filterStatus, filterCategoryIds, servicesOrder, servicesDesc, onPage]);

    useEffect(() => {
        const _page = g?.paginator?.[location.pathname]?.currentPage || 1;
        if (page !== _page) setPage(_page);
    }, [g?.paginator?.[location.pathname]?.currentPage, g?.paginator?.[location.pathname]?.countRows, g?.paginator?.[location.pathname]?.onPage]);

    return (
        <Row>
            <Col className="col-sticky" sm={12}>
                <div className="w-100 d-sm-flex pt-1 justify-content-between align-items-start">
                    <div>
                        <h4 className="p-0 m-0 mb-0 me-2 text-break">{t('title')}</h4>
                        <TitleCounter />
                    </div>
                    <div className="mt-2 mt-sm-0">
                        <ServiceToolBar
                            filterStatus={filterStatus}
                            setFilterStatus={setFilterStatus}
                            filterCategoryIds={filterCategoryIds}
                            setFilterCategoryIds={setFilterCategoryIds}
                            fetchServices={fetchServices}
                        />
                    </div>
                </div>
            </Col>

            <Col sm={12}>
                <div className="px-0">
                    {servicesList ? (
                        <ServicesTable servicesList={servicesList} fetchServices={fetchServices} />
                    ) : (
                        <ESpinner />
                    )}
                </div>
            </Col>
        </Row>
    );
};

export default Services;
